
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { getCurrentLocation, getAddressFromCoordinates } from "@/services/locationService";
import { 
  createTimeEntry, 
  updateTimeEntry, 
  getActiveTimeEntry,
  TimeEntry
} from "@/services/timeEntryService";
import { User } from "@/lib/auth";

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  address?: string;
}

interface BreakEntry {
  startTime: string;
  endTime?: string;
  duration?: number;
}

export const usePointageLogic = (user: User) => {
  const [isTracking, setIsTracking] = useState(false);
  const [startTime, setStartTime] = useState<string>("");
  const [selectedWorksite, setSelectedWorksite] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [currentEntryId, setCurrentEntryId] = useState<string | null>(null);
  
  // États pour la gestion des pauses
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState<string | null>(null);
  const [breakEntries, setBreakEntries] = useState<BreakEntry[]>([]);

  // Vérifier s'il y a un pointage actif au chargement
  useEffect(() => {
    const checkActiveTracking = async () => {
      try {
        const today = format(new Date(), "yyyy-MM-dd");
        const activeEntry = await getActiveTimeEntry(user.id, today);
        
        if (activeEntry) {
          setIsTracking(true);
          setStartTime(activeEntry.start_time);
          setSelectedWorksite(activeEntry.worksite_id);
          setCurrentEntryId(activeEntry.id);
          
          // Restaurer les pauses si elles existent
          if (activeEntry.breaks && activeEntry.breaks.length > 0) {
            setBreakEntries(activeEntry.breaks);
            
            // Vérifier si une pause est en cours
            const lastBreak = activeEntry.breaks[activeEntry.breaks.length - 1];
            if (lastBreak && !lastBreak.endTime) {
              setIsOnBreak(true);
              setBreakStartTime(lastBreak.startTime);
            }
          }
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du pointage actif:', error);
      }
    };

    checkActiveTracking();
  }, [user.id]);

  // Gestion des pauses
  const handleStartBreak = async () => {
    if (!isTracking || !currentEntryId) return;
    
    const now = new Date();
    const breakTime = format(now, "HH:mm");
    
    setIsOnBreak(true);
    setBreakStartTime(breakTime);
    
    // Ajouter l'entrée de pause
    const newBreakEntry: BreakEntry = {
      startTime: breakTime
    };
    const updatedBreaks = [...breakEntries, newBreakEntry];
    setBreakEntries(updatedBreaks);

    // Mettre à jour en base de données
    try {
      await updateTimeEntry(currentEntryId, {
        breaks: updatedBreaks
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la pause:', error);
      toast.error("Erreur lors de la sauvegarde de la pause");
    }
  };

  const handleEndBreak = async () => {
    if (!isOnBreak || !breakStartTime || !currentEntryId) return;
    
    const now = new Date();
    const endTime = format(now, "HH:mm");
    
    // Calculer la durée de la pause
    const startParts = breakStartTime.split(':').map(Number);
    const endParts = endTime.split(':').map(Number);
    const startMinutes = startParts[0] * 60 + startParts[1];
    const endMinutes = endParts[0] * 60 + endParts[1];
    const duration = endMinutes - startMinutes;
    
    // Mettre à jour la dernière entrée de pause
    const updatedBreaks = breakEntries.map((entry, index) => 
      index === breakEntries.length - 1 
        ? { ...entry, endTime, duration }
        : entry
    );
    setBreakEntries(updatedBreaks);
    
    setIsOnBreak(false);
    setBreakStartTime(null);

    // Mettre à jour en base de données
    try {
      await updateTimeEntry(currentEntryId, {
        breaks: updatedBreaks
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la fin de pause:', error);
      toast.error("Erreur lors de la sauvegarde de la fin de pause");
    }
  };

  const handleWorksiteChange = (worksiteId: string) => {
    setSelectedWorksite(worksiteId);
  };

  // Fonction pour démarrer le pointage
  const handleStartTracking = async (comment?: string) => {
    if (!selectedWorksite) {
      toast.error("Veuillez sélectionner un chantier");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Récupération de la position
      let position;
      try {
        position = await getCurrentLocation();
      } catch (locationError) {
        if (isOffline) {
          position = {
            latitude: 0,
            longitude: 0,
            accuracy: 0
          };
          toast.warning("Position non disponible en mode hors-ligne");
        } else {
          throw locationError;
        }
      }
      
      // Démarrage du pointage
      const now = new Date();
      const startTimeStr = format(now, "HH:mm");
      const dateStr = format(now, "yyyy-MM-dd");
      
      // Créer l'entrée de pointage
      const timeEntry = await createTimeEntry({
        user_id: user.id,
        worksite_id: selectedWorksite,
        date: dateStr,
        start_time: startTimeStr,
        start_coordinates: position,
        comment: comment,
        breaks: [],
        status: 'active'
      });
      
      setStartTime(startTimeStr);
      setIsTracking(true);
      setLocationData(position);
      setCurrentEntryId(timeEntry.id);
      
      toast.success("Pointage démarré avec succès", {
        description: comment 
          ? `Commentaire: ${comment}` 
          : `Position enregistrée: ${position.latitude.toFixed(5)}, ${position.longitude.toFixed(5)}`,
      });
      
      // Récupérer l'adresse en arrière-plan si en ligne
      if (!isOffline) {
        setIsAddressLoading(true);
        
        try {
          const address = await getAddressFromCoordinates(position.latitude, position.longitude);
          setLocationData(prev => prev ? {...prev, address} : null);
        } catch (addressError) {
          console.error("Erreur lors de la récupération de l'adresse", addressError);
        } finally {
          setIsAddressLoading(false);
        }
      }
      
    } catch (error) {
      console.error('Erreur lors du démarrage du pointage:', error);
      if (error instanceof Error) {
        setLocationError(error.message);
        toast.error("Erreur lors du démarrage du pointage", {
          description: error.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour terminer le pointage
  const handleEndTracking = async (comment?: string) => {
    if (!currentEntryId) return;
    
    setIsLoading(true);
    
    try {
      // Récupération de la position de fin
      let position;
      try {
        position = await getCurrentLocation();
      } catch (locationError) {
        if (isOffline) {
          position = {
            latitude: 0,
            longitude: 0,
            accuracy: 0
          };
          toast.warning("Position non disponible en mode hors-ligne");
        } else {
          throw locationError;
        }
      }
      
      // Terminer la pause si en cours
      if (isOnBreak) {
        await handleEndBreak();
      }
      
      // Mettre à jour l'entrée de pointage
      const endTimeStr = format(new Date(), "HH:mm");
      const finalComment = comment ? 
        (await getActiveTimeEntry(user.id, format(new Date(), "yyyy-MM-dd")))?.comment ? 
          `${(await getActiveTimeEntry(user.id, format(new Date(), "yyyy-MM-dd")))?.comment}\n\nFin: ${comment}` : 
          comment : 
        undefined;
      
      await updateTimeEntry(currentEntryId, {
        end_time: endTimeStr,
        end_coordinates: position,
        comment: finalComment,
        status: 'completed'
      });
      
      setLocationData(position);
      setIsTracking(false);
      setCurrentEntryId(null);
      setBreakEntries([]);
      
      toast.success("Pointage terminé avec succès", {
        description: comment 
          ? `Commentaire: ${comment}` 
          : `Position enregistrée: ${position.latitude.toFixed(5)}, ${position.longitude.toFixed(5)}`,
      });
      
      // Récupérer l'adresse en arrière-plan si en ligne
      if (!isOffline) {
        setIsAddressLoading(true);
        
        try {
          const address = await getAddressFromCoordinates(position.latitude, position.longitude);
          setLocationData(prev => prev ? {...prev, address} : null);
        } catch (addressError) {
          console.error("Erreur lors de la récupération de l'adresse", addressError);
        } finally {
          setIsAddressLoading(false);
        }
      }
      
    } catch (error) {
      console.error('Erreur lors de la fin du pointage:', error);
      if (error instanceof Error) {
        setLocationError(error.message);
        toast.error("Erreur lors de la fin du pointage", {
          description: error.message,
        });
      }
      setIsTracking(false);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // États
    isTracking,
    startTime,
    selectedWorksite,
    isLoading,
    locationData,
    locationError,
    isAddressLoading,
    isOffline,
    setIsOffline,
    isOnBreak,
    breakStartTime,
    breakEntries,
    
    // Actions
    handleWorksiteChange,
    handleStartTracking,
    handleEndTracking,
    handleStartBreak,
    handleEndBreak,
  };
};
